import { acquisitionAmountToNumber } from '../acquisitionAmount';
import { PRESTITO_ASKS, PrestitoFormValues } from './prestitoForm';

/**
 * Why a compiled profile cannot become a Prestito lead. These are the backend's
 * own `details.reason` identifiers: the browser evaluates the same policy only
 * so the operator learns the answer while the customer is still on the phone,
 * and `422 ineligible_profile` is explained with this very map. A reason the
 * two sides spell differently would be a divergence nobody could see.
 */
export type PrestitoIneligibilityReason =
  | 'guarantor_required'
  | 'insufficient_pension'
  | 'late_payment'
  | 'cdq_alternative';

/**
 * What the operator reads. Each sentence says which answer decided it and that
 * the lead is not acquired: there is no override here, because the backoffice
 * does not get to run an underwriting policy of its own.
 */
export const PRESTITO_INELIGIBILITY_MESSAGE: Record<
  PrestitoIneligibilityReason,
  string
> = {
  guarantor_required:
    'Un lavoratore autonomo con un anno o meno di attività è idoneo solo con un garante. Senza garante il profilo non è idoneo e la lead Prestito non può essere acquisita.',
  insufficient_pension:
    'Una pensione mensile di €700 o meno non raggiunge il reddito minimo previsto per un Prestito. Il profilo non è idoneo e la lead non può essere acquisita.',
  late_payment:
    'Un ritardo nei pagamenti su un finanziamento in corso rende il profilo non idoneo per un Prestito. La lead non può essere acquisita.',
  cdq_alternative:
    'Con un ritardo nei pagamenti questo profilo non è idoneo per un Prestito. Potrebbe rientrare nei requisiti di una Cessione del Quinto, da valutare come richiesta separata: la lead Prestito non può essere acquisita.',
};

/** A pension must exceed this to qualify; €700 itself does not. */
const PRESTITO_PENSION_FLOOR = 700;

/**
 * The answers the qualification policy reads. It is a subset of the draft on
 * purpose: nothing about the customer's identity, contacts or residence can
 * change the outcome, so none of it is in scope here.
 */
export type PrestitoEligibilityAnswers = Pick<
  PrestitoFormValues,
  | 'employmentContract'
  | 'yearsTaxNumber'
  | 'hasGuarantor'
  | 'monthlyIncomePensioner'
  | 'hasOtherFinancing'
  | 'hasLatePreviousLoan'
  | 'employeesNumber'
>;

/**
 * The public product's qualification policy, mirrored. It answers only for
 * answers that are actually there: a half-compiled draft is not yet ineligible,
 * it is unfinished, and saying otherwise would reject a customer over a
 * question the operator has not asked yet.
 *
 * Reasons are decided in the order the questions are asked, so the explanation
 * names the first answer that closed the door rather than an arbitrary one.
 */
export const prestitoIneligibility = (
  answers: PrestitoEligibilityAnswers
): PrestitoIneligibilityReason | null => {
  const contract = answers.employmentContract;

  if (
    contract === 'Lavoratore Autonomo/P.iva' &&
    answers.yearsTaxNumber === '1 anno o meno' &&
    answers.hasGuarantor === 'no'
  ) {
    return 'guarantor_required';
  }

  if (contract === 'Pensionato') {
    const pension = acquisitionAmountToNumber(answers.monthlyIncomePensioner);
    if (pension !== null && pension <= PRESTITO_PENSION_FLOOR) {
      return 'insufficient_pension';
    }
  }

  if (
    !PRESTITO_ASKS.hasLatePreviousLoan(answers) ||
    answers.hasLatePreviousLoan !== 'si'
  ) {
    return null;
  }

  // A pensioner with a late payment is out of the Prestito policy, but the
  // Cessione del Quinto may still take them, which is why the shared policy
  // keeps this reason apart from a plain rejection.
  if (contract === 'Pensionato') return 'cdq_alternative';

  if (contract !== 'Dipendente tempo indeterminato') return 'late_payment';

  // Everyone else is out on the late payment alone; only a permanent employee
  // is asked how large their employer is, and the branch is described once, by
  // the question's own definition.
  if (!PRESTITO_ASKS.employeesNumber(answers)) return 'late_payment';

  // The size question is open until it is answered, and an unanswered question
  // rejects nobody.
  if (!answers.employeesNumber) return null;

  // Neither size currently qualifies; it only decides whether a Cessione del
  // Quinto remains conceivable.
  return answers.employeesNumber === 'Più di 15 dip'
    ? 'cdq_alternative'
    : 'late_payment';
};
